import { useState, useCallback, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useUserContext } from "../hooks/UserContext";
import { useAppConfig } from "../hooks/AppConfigContext";
import { fetchGetProductoDetalleAdministracion, fetchUpdateProductoPrecios } from "../services/productoService";
import LocalSelector from "./components/LocalSelector";
import { CurrencyFormatter } from "../utils/CurrencyFormatter";
import { isAbortError } from "../services/fetchService";
import { useNotification } from "../hooks/NotificationContext";

const getValue = (source: any, ...keys: string[]) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return "";
};

const toBoolean = (value: unknown) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "1" || normalized === "true" || normalized === "si";
  }
  return false;
};

const formatBoolean = (value: unknown) => toBoolean(value) ? "Si" : "No";

const formatMoneyValue = (value: unknown) => {
  if (value === undefined || value === null || value === "" || !Number.isFinite(parseDecimalValue(value))) return "-";
  const numericValue = parseDecimalValue(value);
  return Number.isFinite(numericValue) ? CurrencyFormatter.format(numericValue) : String(value);
};

const parseDecimalValue = (value: unknown) => {
  if (typeof value === "number") return value;
  if (value === undefined || value === null || value === "") return NaN;
  return Number(String(value).replace(/\./g, "").replace(",", "."));
};

const formatDifference = (currentValue: number, newValue: number) => {
  if (!Number.isFinite(currentValue) || !Number.isFinite(newValue)) return "-";
  const difference = newValue - currentValue;
  const percent = currentValue !== 0 ? difference * 100 / currentValue : 0;
  const sign = difference > 0 ? "+" : "";
  return `${sign}${CurrencyFormatter.format(difference)} (${sign}${percent.toFixed(2)}%)`;
};

const resolveNextPriceValue = (typedValue: string, currentValue: number) => {
  const parsed = parseDecimalValue(typedValue);
  return Number.isFinite(parsed) ? parsed : currentValue;
};

const normalizeListField = (value: unknown) => String(value ?? "").trim().toUpperCase();

const getRelatedPriceLists = (relacionListas: string | undefined): Record<string, number> => {
  if (!relacionListas?.trim()) return {};

  try {
    const parsed = JSON.parse(relacionListas);
    const relaciones = Array.isArray(parsed?.relaciones) ? parsed.relaciones : [];
    return relaciones.reduce((acc: Record<string, number>, item: any) => {
      const field = normalizeListField(item.lista_original ?? item.campo_en_bd ?? item.lista);
      const percentage = parseDecimalValue(item.porcentaje);
      if (field && Number.isFinite(percentage) && percentage !== 0) {
        acc[field] = percentage;
      }
      return acc;
    }, {});
  } catch {
    return {};
  }
};

const applyRelation = (baseValue: number, percentage: number | undefined) => {
  if (!Number.isFinite(baseValue) || percentage === undefined) return baseValue;
  return Math.round(baseValue * (1 + percentage / 100) * 100) / 100;
};

const InfoItem = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600} sx={{ wordBreak: "break-word" }}>
      {value || "-"}
    </Typography>
  </Box>
);

const UpdateProducto = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getUser } = useUserContext();
  const { showNotification } = useNotification();
  const { config, productoVentaHeaders } = useAppConfig();
  const user = useMemo(() => getUser(), [getUser]);
  const userToken = user?.token;
  const contadoHeader = productoVentaHeaders.CONTADO?.Header ?? "Contado";
  const mayorHeader = productoVentaHeaders.MAYOR?.Header ?? "Mayor";
  const menorHeader = productoVentaHeaders.MENOR?.Header ?? "Menor";
  const relatedPriceLists = useMemo(() => getRelatedPriceLists(config?.relacionListas), [config?.relacionListas]);
  const isMayorRelated = relatedPriceLists.MAYOR !== undefined;
  const isMenorRelated = relatedPriceLists.MENOR !== undefined;

  // Permission check
  const hasPermission = user?.permissions?.includes("actualizar_precios");

  // State
  const [idLocal, setIdLocal] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [producto, setProducto] = useState<any>(null);
  const [precioContado, setPrecioContado] = useState<string>("");
  const [precioMayor, setPrecioMayor] = useState<string>("");
  const [precioMenor, setPrecioMenor] = useState<string>("");

  const loadProductoDetalle = useCallback(async (signal?: AbortSignal) => {
    if (!user || !userToken || !idLocal || !id) return;

    setLoading(true);
    try {
      const data = await fetchGetProductoDetalleAdministracion(user, idLocal, id, signal);
      setProducto(data);
      
      // Set initial values
      setPrecioContado(data.CONTADO?.toString() || "");
      setPrecioMayor(data.MAYOR?.toString() || "");
      setPrecioMenor(data.MENOR?.toString() || "");
    } catch (error) {
      if (isAbortError(error)) return;
      showNotification("Error al cargar el producto", "error");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [id, idLocal, showNotification, user, userToken]);

  // Load product details when idLocal and id are available
  useEffect(() => {
    if (idLocal && id && userToken) {
      const controller = new AbortController();
      loadProductoDetalle(controller.signal);
      return () => controller.abort();
    }
  }, [idLocal, id, userToken, loadProductoDetalle]);

  const handleLocalChange = (newIdLocal: string | undefined) => {
    setIdLocal(newIdLocal);
  };

  const handleSave = async () => {
    if (!user || !idLocal || !id) {
      showNotification("Debe seleccionar un local", "error");
      return;
    }

    const currentContado = parseDecimalValue(getValue(producto, "CONTADO", "contado"));
    const currentMayor = parseDecimalValue(getValue(producto, "MAYOR", "mayor"));
    const currentMenor = parseDecimalValue(getValue(producto, "MENOR", "menor"));
    if (!Number.isFinite(currentContado) || !Number.isFinite(currentMayor) || !Number.isFinite(currentMenor)) {
      showNotification("No se pudieron obtener los precios actuales del producto", "error");
      return;
    }

    setSaving(true);
    try {
      await fetchUpdateProductoPrecios(user, {
        id_producto: parseInt(id),
        id_local: parseInt(idLocal),
        precio_contado: resolveNextPriceValue(precioContado, currentContado),
        precio_mayor: isMayorRelated
          ? applyRelation(resolveNextPriceValue(precioContado, currentContado), relatedPriceLists.MAYOR)
          : resolveNextPriceValue(precioMayor, currentMayor),
        precio_menor: isMenorRelated
          ? applyRelation(resolveNextPriceValue(precioContado, currentContado), relatedPriceLists.MENOR)
          : resolveNextPriceValue(precioMenor, currentMenor),
      });

      showNotification("Precios actualizados exitosamente", "success");
      
      // Navigate back after a short delay
      setTimeout(() => {
        navigate(-1);
      }, 1500);
    } catch (error) {
      showNotification("Error al actualizar los precios", "error");
    } finally {
      setSaving(false);
    }
  };

  const productoInfo = useMemo(() => {
    if (!producto) return null;

    return {
      idProducto: getValue(producto, "IDPRODUCTO", "idProducto"),
      codigo: getValue(producto, "CODIGO", "codigo") || id,
      producto: getValue(producto, "PRODUCTO", "producto") || "Producto",
      descripcion: getValue(producto, "DESCRIPCION", "descripcion"),
      presentacion: getValue(producto, "PRESENTACION", "presentacion"),
      uxB: getValue(producto, "UxB", "UXB", "uxB"),
      stock: getValue(producto, "STOCK", "stock"),
      precioCompra: getValue(producto, "PRECIOCOMPRA", "PRECIO COMPRA", "precioCompra"),
      ivaVenta: getValue(producto, "IVAVENTA", "ivaVenta"),
      web: getValue(producto, "WEB", "web"),
      idBalanza: getValue(producto, "IDBALANZA", "idBalanza"),
      idReceta: Number(getValue(producto, "IDRECETA", "idReceta") || 0),
      bajarPrecio: toBoolean(getValue(producto, "BAJARPRECIO", "bajarPrecio")),
      llevaSubproductos: toBoolean(getValue(producto, "LLEVASUBPRODUCTOS", "llevaSubproductos")),
      venderSinStock: toBoolean(getValue(producto, "VENDERSINSTOCK", "venderSinStock")),
      alquilable: toBoolean(getValue(producto, "ALQUILABLE", "alquilable")),
      esServicio: toBoolean(getValue(producto, "ESSERVICIO", "esServicio")),
      seProduce: toBoolean(getValue(producto, "SEPRODUCE", "seProduce")),
    };
  }, [id, producto]);

  const pricePreview = useMemo(() => {
    if (!producto) return [];

    return [
      {
        label: contadoHeader,
        current: parseDecimalValue(getValue(producto, "CONTADO", "contado")),
        typed: precioContado,
        field: "CONTADO",
      },
      {
        label: mayorHeader,
        current: parseDecimalValue(getValue(producto, "MAYOR", "mayor")),
        typed: precioMayor,
        field: "MAYOR",
      },
      {
        label: menorHeader,
        current: parseDecimalValue(getValue(producto, "MENOR", "menor")),
        typed: precioMenor,
        field: "MENOR",
      },
    ].map((price) => {
      const currentContado = parseDecimalValue(getValue(producto, "CONTADO", "contado"));
      const nextContado = resolveNextPriceValue(precioContado, currentContado);
      const relatedPercentage = relatedPriceLists[price.field];
      const next = relatedPercentage !== undefined
        ? applyRelation(nextContado, relatedPercentage)
        : resolveNextPriceValue(price.typed, price.current);

      return {
        ...price,
        next,
        relatedPercentage,
        changed: Number.isFinite(price.current) && next !== price.current,
      };
    });
  }, [contadoHeader, mayorHeader, menorHeader, precioContado, precioMayor, precioMenor, producto, relatedPriceLists]);

  const hasPriceChanges = pricePreview.some((price) => price.changed);

  // Permission check
  if (!hasPermission) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">No tiene permisos para actualizar precios de productos</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Actualizar Precios de Producto
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <LocalSelector onLocalChange={handleLocalChange} />
        </Grid>
      </Grid>

      {loading ? (
        <Box sx={{ textAlign: "center", mt: 3 }}>
          <CircularProgress />
        </Box>
      ) : productoInfo ? (
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} lg={7}>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {productoInfo.producto}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Codigo: {productoInfo.codigo}
                  </Typography>
                </Box>
                <Chip label={`ID ${productoInfo.idProducto || id}`} size="small" />
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <InfoItem label="Descripcion" value={productoInfo.descripcion} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <InfoItem label="Stock" value={productoInfo.stock} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <InfoItem label="Presentacion" value={productoInfo.presentacion} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <InfoItem label="Unidades x bulto" value={productoInfo.uxB} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <InfoItem label="IVA venta" value={productoInfo.ivaVenta} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <InfoItem label="Precio compra" value={formatMoneyValue(productoInfo.precioCompra)} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <InfoItem label="ID balanza" value={productoInfo.idBalanza || "-"} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <InfoItem label="ID receta" value={productoInfo.idReceta || "-"} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <InfoItem label="Web" value={productoInfo.web || "-"} />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`Baja precio: ${formatBoolean(productoInfo.bajarPrecio)}`} />
                <Chip size="small" label={`Vende sin stock: ${formatBoolean(productoInfo.venderSinStock)}`} />
                <Chip size="small" label={`Subproductos: ${formatBoolean(productoInfo.llevaSubproductos)}`} />
                <Chip size="small" label={`Se produce: ${formatBoolean(productoInfo.seProduce)}`} />
                <Chip size="small" label={`Alquilable: ${formatBoolean(productoInfo.alquilable)}`} />
                <Chip size="small" label={`Servicio: ${formatBoolean(productoInfo.esServicio)}`} />
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Precios de venta
              </Typography>
              <TableContainer sx={{ mb: 2 }}>
                <Table size="small" aria-label="Previsualizacion de cambios de precios">
                  <TableHead>
                    <TableRow>
                      <TableCell>Lista</TableCell>
                      <TableCell align="right">Actual</TableCell>
                      <TableCell align="right">Nuevo</TableCell>
                      <TableCell align="right">Diferencia</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pricePreview.map((price) => (
                      <TableRow key={price.label}>
                        <TableCell component="th" scope="row">
                          {price.label}
                        </TableCell>
                        <TableCell align="right">{formatMoneyValue(price.current)}</TableCell>
                        <TableCell align="right">
                          <Typography
                            component="span"
                            variant="body2"
                            fontWeight={price.changed ? 700 : 500}
                            color={price.changed ? "primary.main" : "text.primary"}
                          >
                            {formatMoneyValue(price.next)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            component="span"
                            variant="body2"
                            color={price.changed ? (price.next > price.current ? "success.main" : "error.main") : "text.secondary"}
                          >
                            {price.changed ? formatDifference(price.current, price.next) : "Sin cambio"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {!hasPriceChanges && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Los precios ingresados coinciden con los precios actuales.
                </Alert>
              )}
              {(isMayorRelated || isMenorRelated) && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Las listas relacionadas se calculan desde {contadoHeader} y no pueden modificarse manualmente.
                </Alert>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label={contadoHeader}
                    type="number"
                    value={precioContado}
                    onChange={(e) => setPrecioContado(e.target.value)}
                    fullWidth
                    inputProps={{ step: "0.01", min: "0" }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={mayorHeader}
                    type="number"
                    value={precioMayor}
                    onChange={(e) => setPrecioMayor(e.target.value)}
                    fullWidth
                    disabled={isMayorRelated}
                    helperText={isMayorRelated ? `Relacionado a ${contadoHeader} (${relatedPriceLists.MAYOR}%)` : undefined}
                    inputProps={{ step: "0.01", min: "0" }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={menorHeader}
                    type="number"
                    value={precioMenor}
                    onChange={(e) => setPrecioMenor(e.target.value)}
                    fullWidth
                    disabled={isMenorRelated}
                    helperText={isMenorRelated ? `Relacionado a ${contadoHeader} (${relatedPriceLists.MENOR}%)` : undefined}
                    inputProps={{ step: "0.01", min: "0" }}
                  />
                </Grid>
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Button
                        onClick={() => navigate(-1)}
                        variant="outlined"
                        fullWidth
                        disabled={saving}
                      >
                        Cancelar
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        onClick={handleSave}
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={saving}
                        startIcon={saving ? <CircularProgress color="inherit" size={18} /> : undefined}
                      >
                        {saving ? "Guardando..." : "Guardar"}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      ) : (
        idLocal && (
          <Alert severity="info">Cargando producto...</Alert>
        )
      )}
    </Box>
  );
};

export default UpdateProducto;


